// ./src/components/generic/MediaSelector/tabs/UploadTab.tsx
//
// "Upload" — bring in a new file (drag-drop / browse). For raster images the
// crop/rotate editor is offered before upload (skippable); everything else
// uploads as-is. Every upload is tagged with the intent's context_path +
// purpose + the uploader identity, so it lands correctly in ms_storage.

import React, { useCallback, useState } from "react";
import { Stack, Alert } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { DEFAULT_PAGE_SIZE } from "@/config";
import { makeUniqueFilename } from "@/utils/nameHelpers";
import { UploadedFile } from "@/utils/api/UploadedFileService";
import { useUploadedFiles } from "@/hooks/useUploadedFiles";

import { FileUpload } from "../../FileUpload";
import { ImageCropEditor, editedImageMime, imageFileName } from "../../ImageCropEditor";
import { MediaCropAspect, MediaKind, isEditableImage } from "../mediaSelectorConfig";

export interface UploadTabUploader {
  kind: string;
  id?: string | null;
  name?: string | null;
}

export interface UploadTabProps {
  kind: MediaKind;
  /** `accept` for the drop zone. */
  accept: string;
  /** Storage purpose tag for the upload. */
  purpose: string;
  /** Crop aspect for the image editor. */
  cropAspect: MediaCropAspect;
  /** Owning-context path to tag the upload with, e.g. `e/{eventId}` (see EventFileService). */
  contextPath?: string | null;
  /** Uploader identity recorded on the file. */
  uploader: UploadTabUploader;
  /** Max upload size in bytes. */
  maxFileSize?: number;
  /** Called with the stored file after a successful upload. */
  onUploaded: (file: UploadedFile) => void;
}

export const UploadTab: React.FC<UploadTabProps> = ({
  kind,
  accept,
  purpose,
  cropAspect,
  contextPath,
  uploader,
  maxFileSize = 100 * 1024 * 1024,
  onUploaded,
}) => {
  const { t } = useTranslation(["common"]);

  const { create, isCreating } = useUploadedFiles({ autoLoad: false, pageSize: DEFAULT_PAGE_SIZE });

  const [error, setError] = useState<string | null>(null);
  const [cropImage, setCropImage] = useState<Blob | null>(null);
  const [cropName, setCropName] = useState<string>("image");

  const doUpload = useCallback(
    async (blob: Blob, filename: string, mimeType: string) => {
      setError(null);

      const file = blob instanceof File ? blob : new File([blob], filename, { type: mimeType });
      const uniqueName = makeUniqueFilename(filename);

      const result = await create({
        files: [file],
        name: uniqueName,
        originalFilename: filename,
        mimeType,
        contextPath: contextPath ?? undefined,
        purpose,
        uploadedByKind: uploader.kind,
        uploadedById: uploader.id ?? undefined,
        uploadedByName: uploader.name ?? undefined,
      });

      if (result) {
        onUploaded(result);
      } else {
        setError(
          t("common:mediaSelector.uploadFailed", { defaultValue: "Upload failed. Please try again." })
        );
      }
    },
    [create, contextPath, purpose, uploader, onUploaded, t]
  );

  const handleFilesSelected = useCallback(
    (files: File[]) => {
      const file = files[0];
      if (!file) {
        return;
      }

      // Images (that we can decode) go through the editor first; skip is offered
      // inside it. Everything else uploads straight away.
      if (kind === "image" && isEditableImage(file)) {
        setCropName(file.name);
        setCropImage(file);
        return;
      }

      doUpload(file, file.name, file.type || "application/octet-stream");
    },
    [kind, doUpload]
  );

  const handleCropConfirm = useCallback(
    (blob: Blob) => {
      // Named and typed as what the editor produced: a PNG when the picture is transparent (#135), never always `.jpg`.
      const mime = editedImageMime(blob);
      setCropImage(null);
      doUpload(blob, imageFileName(cropName, mime), mime);
    },
    [cropName, doUpload]
  );

  const handleCropSkip = useCallback(
    (blob: Blob) => {
      setCropImage(null);
      // Skip hands back the original: an untyped one is typed by its name, never called a JPEG by default.
      doUpload(blob, cropName, editedImageMime(blob, cropName));
    },
    [cropName, doUpload]
  );

  return (
    <Stack gap="sm">
      {error && (
        <Alert color="red" variant="light" onClose={() => setError(null)} withCloseButton>
          {error}
        </Alert>
      )}

      <FileUpload
        accept={accept}
        multiple={false}
        maxSize={maxFileSize}
        uploading={isCreating}
        disabled={isCreating}
        onFilesSelected={handleFilesSelected}
      />

      <ImageCropEditor
        opened={cropImage !== null}
        image={cropImage}
        aspectRatio={cropAspect}
        onClose={() => setCropImage(null)}
        onConfirm={handleCropConfirm}
        onSkip={handleCropSkip}
      />
    </Stack>
  );
};
